import { ActionKey, PagePath } from '@pokerroom/types';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import './display-console.css';
import { useTranslation } from 'react-i18next';
import { useTournamentConsole } from '../tournament/useTournamentConsole';
import { useAuthStore } from '../../store/auth.store';
import { useDialogFocus } from '../../hooks/useDialogFocus';
import { api } from '../../lib/api';
import { formatTime } from '../../lib/format';

export interface ConsoleScreen {
  id: string;
  name: string;
  tournamentId: string | null;
  layout: string;
  isActive: boolean;
}
type Model = ReturnType<typeof useTournamentConsole>;
type Command = () => Promise<unknown>;
export function TournamentConsoleGroup({
  id,
  screens,
  render,
}: {
  id: string;
  screens: ConsoleScreen[];
  render: (screen: ConsoleScreen, console: ReactNode) => ReactNode;
}) {
  const auth = useAuthStore();
  const allowed = (action: string) =>
    Boolean(auth.user?.isSystemUser || auth.allowedActions?.includes(action));
  const canRead = Boolean(
    auth.accessToken &&
    (auth.user?.isSystemUser ||
      auth.visiblePages?.some((p) =>
        [PagePath.TOURNAMENT as string, PagePath.TABLES as string].includes(p),
      ) ||
      allowed(ActionKey.TIMER_CONTROL)),
  );
  const model = useTournamentConsole(id, undefined, canRead, true);
  const lock = useRef(false);
  const alive = useRef(true);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<{ screenId: string; message: string } | null>(null);
  const { t } = useTranslation('display');
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  async function run(screen: ConsoleScreen, command: Command) {
    if (
      lock.current ||
      !canRead ||
      !model.connected ||
      model.sessionPaused ||
      !model.timerUpdate ||
      !model.tournament ||
      Boolean(model.socketError) ||
      model.terminal
    )
      return;
    lock.current = true;
    setPending(true);
    setError(null);
    const revision = useAuthStore.getState().authRevision;
    try {
      const fresh = await api.get<ConsoleScreen[]>('/display/screens');
      if (!alive.current || revision !== useAuthStore.getState().authRevision) return;
      if (
        !fresh.some(
          (s) =>
            s.id === screen.id && s.isActive && s.layout === 'TIMER_FULL' && s.tournamentId === id,
        )
      )
        throw new Error(t('console.bindingChanged'));
      await command();
    } catch (cause) {
      if (alive.current && revision === useAuthStore.getState().authRevision)
        setError({
          screenId: screen.id,
          message: cause instanceof Error ? cause.message : t('console.failed'),
        });
    } finally {
      lock.current = false;
      if (alive.current) setPending(false);
    }
  }
  return (
    <>
      {screens.map((screen) =>
        render(
          screen,
          <EmbeddedTimerConsole
            key={`${screen.id}:${id}`}
            screen={screen}
            model={model}
            allowed={allowed}
            disabled={
              !canRead ||
              pending ||
              !model.connected ||
              model.sessionPaused ||
              !model.timerUpdate ||
              !model.tournament ||
              Boolean(model.socketError) ||
              model.terminal
            }
            pending={pending}
            error={
              !canRead ? t('console.noRead') : error?.screenId === screen.id ? error.message : ''
            }
            run={(command) => run(screen, command)}
          />,
        ),
      )}
    </>
  );
}

function EmbeddedTimerConsole({
  screen,
  model: m,
  allowed,
  disabled,
  pending,
  error,
  run,
}: {
  screen: ConsoleScreen;
  model: Model;
  allowed: (action: string) => boolean;
  disabled: boolean;
  pending: boolean;
  error: string;
  run: (command: Command) => Promise<void>;
}) {
  const { t } = useTranslation('display');
  const [confirm, setConfirm] = useState(false);
  const [seconds, setSeconds] = useState('');
  const dialog = useDialogFocus(confirm, () => setConfirm(false), pending);
  const control = allowed(ActionKey.TIMER_CONTROL);
  const startAction =
    m.tournament?.status === 'PAUSED'
      ? ActionKey.TOURNAMENT_RESUME
      : ['DRAFT', 'REGISTERED'].includes(m.tournament?.status ?? '')
        ? ActionKey.TOURNAMENT_START
        : ActionKey.TIMER_CONTROL;
  const start = () =>
    m.tournament?.status === 'PAUSED'
      ? m.resumeMutation.mutateAsync()
      : ['DRAFT', 'REGISTERED'].includes(m.tournament?.status ?? '')
        ? m.startMutation.mutateAsync()
        : m.sendTimerActionAsync({ action: 'start' });
  const button = (
    label: string,
    enabled: boolean,
    command: Command,
    accessibleLabel = label,
    tone = '',
  ) => (
    <button
      type="button"
      aria-label={accessibleLabel}
      disabled={disabled || !enabled}
      onClick={() => void run(command)}
      className={`console-button ${tone}`}
    >
      {label}
    </button>
  );
  const valid = /^\d+$/.test(seconds) && Number(seconds) <= 86400;
  return (
    <section
      className={`embedded-console ${m.terminal ? 'console-terminal' : ''}`}
      aria-label={`${screen.name} ${m.tournament?.name ?? ''}`}
    >
      <header className="console-heading">
        <div>
          <p className="console-eyebrow">
            {screen.name} <span>· {t('layoutTimerFull')}</span>
          </p>
          <h2>{m.tournament?.name ?? t('loading')}</h2>
        </div>
        <span
          className={`console-badge ${m.terminal ? '' : m.tournament?.status === 'IN_PROGRESS' ? 'console-live' : 'console-paused'}`}
        >
          {m.settling
            ? t('console.settling')
            : t(`console.status.${m.tournament?.status ?? 'LOADING'}`)}
        </span>
      </header>
      <div className="console-timer">
        <p className="console-level">
          {m.isBreak ? t('console.break') : `Level ${m.displayLevel}`}
        </p>
        <p className="console-digits">{formatTime(m.remainingSeconds)}</p>
        <div className="console-meta">
          <span>{t('console.players', { active: m.activePlayers, total: m.totalEntries })}</span>
          <span>
            {t('console.prizePool')} <b>{m.prizePool.toLocaleString()}</b>
          </span>
          {m.manualMode && <span>{t('console.manualMode')}</span>}
        </div>
        <div className="console-bets">
          <div>
            <small>SB</small>
            <b>{m.sb.toLocaleString()}</b>
          </div>
          <div className="console-bb">
            <small>BB</small>
            <b>{m.bb.toLocaleString()}</b>
          </div>
          <div>
            <small>Ante / BBA</small>
            <b>{m.anteBba}</b>
          </div>
        </div>
      </div>
      <p role="status" className="console-notice">
        {pending
          ? t('console.pending')
          : !m.connected || m.sessionPaused
            ? t('console.unsynced')
            : m.adjustmentNotice
              ? t('console.adjusted', { seconds: m.adjustmentMutation.variables ?? 0 })
              : ''}
      </p>
      {(!m.connected || m.sessionPaused || m.socketError) && (
        <button
          type="button"
          className="min-h-11 min-w-11 px-3 text-primary"
          onClick={m.retrySocket}
        >
          {t('console.retry')}
        </button>
      )}
      {(error || m.dataError || m.actionError || m.socketError) && (
        <p role="alert" className="text-error">
          {error || m.dataError || m.actionError || m.socketError}
        </p>
      )}
      {m.manualMode && (
        <div className="console-counts">
          {(['entrants', 'eliminated', 'rebuys', 'addons'] as const).map((field, i) => {
            const value = [m.totalEntries, m.eliminatedCount, m.rebuyCount, m.addonCount][i]!;
            const minus = field === 'entrants' ? value > m.eliminatedCount : value > 0;
            return (
              <div key={field} className="console-counter">
                <p>{t(`console.${field}`)}</p>
                <div>
                  {button(
                    '−',
                    control && minus,
                    () => m.countMutation.mutateAsync({ field, delta: -1 }),
                    `${t(`console.${field}`)} −1`,
                  )}
                  <strong>{value}</strong>
                  {button(
                    '+',
                    control && (field !== 'eliminated' || value < m.totalEntries),
                    () => m.countMutation.mutateAsync({ field, delta: 1 }),
                    `${t(`console.${field}`)} +1`,
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="console-controls">
        {button(t('console.previous'), control && (m.currentLevel?.levelNo ?? 1) > 1, () =>
          m.sendTimerActionAsync({ action: 'prev_level' }),
        )}
        {button(
          t('console.start'),
          (['DRAFT', 'REGISTERED'].includes(m.tournament?.status ?? '') || !m.isRunning) &&
            allowed(startAction),
          async () => start(),
          t('console.start'),
          !m.isRunning ? 'console-primary' : '',
        )}
        {button(
          t('console.pause'),
          m.tournament?.status === 'IN_PROGRESS' && allowed(ActionKey.TOURNAMENT_PAUSE),
          () => m.pauseMutation.mutateAsync(),
          t('console.pause'),
          m.isRunning ? 'console-primary' : '',
        )}
        {button(t('console.next'), control && Boolean(m.nextLevel), () =>
          m.sendTimerActionAsync({ action: 'next_level' }),
        )}
        <button
          type="button"
          className="console-button console-finish"
          disabled={disabled || !m.isActive || !allowed(ActionKey.TOURNAMENT_PAUSE)}
          onClick={() => setConfirm(true)}
        >
          {t('console.finish')}
        </button>
      </div>
      <div className="console-adjustments" hidden={m.terminal}>
        <div className="console-section-label">
          {t('console.adjustTime')}
          <span>{t('console.keepLevel')}</span>
        </div>
        <div className="console-adjust-grid">
          {[-60, -180, -300, 60, 180, 300].map((delta) => (
            <span key={delta}>
              {button(
                `${delta > 0 ? '+' : '−'}${Math.abs(delta) / 60} ${t('console.minutes')}`,
                control && m.isActive,
                () => m.adjustmentMutation.mutateAsync(delta),
              )}
            </span>
          ))}
        </div>
        <details className="console-custom">
          <summary className="min-h-11 cursor-pointer">{t('console.custom')}</summary>
          <label className="block">
            {t('console.seconds')}
            <input
              className="min-h-11 w-full rounded border border-base-300 bg-base-200 px-3"
              inputMode="numeric"
              value={seconds}
              onChange={(e) => setSeconds(e.target.value)}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            {button('−', control && m.isActive && valid && Number(seconds) > 0, () =>
              m.adjustmentMutation.mutateAsync(-Number(seconds)),
            )}
            {button('+', control && m.isActive && valid && Number(seconds) > 0, () =>
              m.adjustmentMutation.mutateAsync(Number(seconds)),
            )}
            {button(t('console.setTime'), control && valid, () =>
              m.sendTimerActionAsync({ action: 'set_time', seconds: Number(seconds) }),
            )}
          </div>
        </details>
      </div>
      {m.terminal && <p className="console-retained">{t('console.retained')}</p>}

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-base-content/50 p-4">
          <div
            ref={dialog}
            role="dialog"
            aria-modal="true"
            aria-label={t('console.finish')}
            tabIndex={-1}
            className="console-dialog w-full max-w-md space-y-4 rounded-xl bg-base-200 p-6"
          >
            <h3 className="font-bold text-lg">{t('console.confirmTitle')}</h3>
            <div className="rounded bg-base-300 p-4 font-bold">
              {screen.name} · {m.tournament?.name}
            </div>
            <p>{t('console.confirmFinish')}</p>
            <div className="flex flex-wrap gap-3">
              <button
                className="console-button"
                disabled={pending}
                onClick={() => setConfirm(false)}
              >
                {t('cancel')}
              </button>
              <button
                className="console-button console-finish"
                disabled={disabled}
                onClick={() =>
                  void run(async () => {
                    await m.finalizeMutation.mutateAsync();
                    setConfirm(false);
                  })
                }
              >
                {t('console.finish')}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
